import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import { t, Trans } from '@lingui/macro';
import MinusCircle from '@decisiv/iconix/lib/components/MinusCircle';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import { useNavigate } from 'react-router-dom';
import noop from 'lodash/noop';
import { AffectedList, Bold } from '../style';
import SyncStatusTable from '../SyncStatusTable';
import { locationStateTag } from '../syncStatus';
import ConfiguredIntegrationsApi from '../../../api/configured_integrations';

// Stable default: a fresh literal here would change identity every render
// and re-fire the hooks that depend on it.
const NO_SHARED_SETTINGS = [];

export default function RemoveLocationModal({
  visible,
  onClose,
  integration,
  location,
  sharedSettings = NO_SHARED_SETTINGS,
  sharedSettingsLoading = false,
}) {
  const navigate = useNavigate();
  // Removal discards the configured values, so the blast radius is shown first.
  // A location that opted out of lifecycle sync is left out — the cascade will
  // not touch it.
  const [alsoRemoved, setAlsoRemoved] = useState([]);
  const [lookupState, setLookupState] = useState('idle');

  useEffect(() => {
    const srmAccountId = location.attributes?.srm_account_id;
    // Membership is unknown until the groups load, and an unknown blast radius
    // must not read as an empty one.
    if (!visible || !integration.id || !srmAccountId || sharedSettingsLoading) {
      return;
    }

    const groups = sharedSettings.filter((setting) =>
      (setting.integration_uuids || []).includes(integration.id),
    );
    // A location opted out of lifecycle sync removes nothing elsewhere, so there
    // is no blast radius to look up.
    if (
      groups.length === 0 ||
      location.attributes?.ignore_shared_setting_actions
    ) {
      setAlsoRemoved([]);
      setLookupState('done');
      return;
    }

    const nameByUuid = Object.fromEntries(
      groups.flatMap((setting) =>
        (setting.integrations || []).map((i) => [i.uuid, i.name]),
      ),
    );
    const peerUuids = new Set(
      groups
        .flatMap((setting) => setting.integration_uuids || [])
        .filter((uuid) => uuid !== integration.id),
    );

    async function loadAffected() {
      setLookupState('loading');
      const { data } =
        await ConfiguredIntegrationsApi.fetchAllConfiguredIntegrationsBySrmAccountId(
          srmAccountId,
        );

      if (!data) {
        setAlsoRemoved([]);
        setLookupState('failed');
        return;
      }

      setLookupState('done');
      setAlsoRemoved(
        data
          .filter(
            (ci) =>
              peerUuids.has(ci.attributes.integration_uuid) &&
              !ci.attributes.ignore_shared_setting_actions,
          )
          .map((ci) => ({
            id: ci.id,
            name:
              nameByUuid[ci.attributes.integration_uuid] ||
              ci.attributes.integration_uuid,
            // Where the location stands today in that integration — what the
            // removal is about to discard.
            ...locationStateTag(ci.attributes),
          })),
      );
    }

    loadAffected();
  }, [
    visible,
    integration.id,
    location.attributes?.srm_account_id,
    location.attributes?.ignore_shared_setting_actions,
    sharedSettings,
    sharedSettingsLoading,
  ]);

  const notificationSuccessProps = {
    intent: 'success',
    title: t`Configured Integrations`,
    duration: 3500,
    onClose: noop,
  };
  const notificationErrorProps = {
    intent: 'danger',
    title: t`Something went wrong!`,
    duration: 3500,
    onClose: noop,
  };

  const { notify } = useNotifications();

  const deleteConfiguredIntegration = useCallback(async () => {
    const resp =
      await ConfiguredIntegrationsApi.deleteConfiguredIntegrationByUuid(
        location?.id,
      );

    if (resp.status === 200) {
      notify(
        notificationSuccessProps,
        t`The location was removed successfully`,
      );

      navigate(`/integrations/${integration?.id}`);
    } else {
      notify(
        { ...notificationErrorProps, title: resp.errors[0].title },
        resp.errors[0].detail,
      );
    }
  }, [
    integration?.id,
    location?.id,
    notificationErrorProps,
    notificationSuccessProps,
    notify,
  ]);

  const integrationName = integration.attributes?.name;
  const integrationEnabled = integration.attributes?.enabled;
  const locationName = location.attributes?.name;
  const locationId = location.attributes?.srm_account_id;

  return (
    <Modal
      actions={[
        {
          text: t`Remove Integration`,
          onClick: () => deleteConfiguredIntegration(),
          // Confirming before the blast radius is known would defeat the point
          // of showing it.
          disabled: lookupState === 'idle' || lookupState === 'loading',
        },
        {
          text: t`Cancel`,
          onClick: onClose,
        },
      ]}
      icon={MinusCircle}
      intent="danger"
      onClose={onClose}
      title={t`Remove ${integrationName} Integration from ${locationName} ?`}
      visible={visible}
    >
      <Flex flexDirection="column">
        <P shade={1} marginTop={1} marginBottom={1}>
          <Trans>
            You are about to remove the {integrationName} Integration (currently{' '}
            {integrationEnabled ? t`enabled` : t`disabled`}) from
            <Bold>
              {`${locationName}`} ({locationId})
            </Bold>
            .
          </Trans>
        </P>
        <P shade={1}>
          <Trans>
            When you click REMOVE INTEGRATION, the integration is disabled for
            the location and disappears from the integrations’s list of
            locations. You can always add it back.
          </Trans>
        </P>

        {lookupState === 'failed' && (
          <AffectedList>
            <P weight="semibold" marginBottom={0}>
              <Trans>
                We could not check which linked integrations this also removes.
                Close and try again rather than removing blind.
              </Trans>
            </P>
          </AffectedList>
        )}

        {alsoRemoved.length > 0 && (
          <AffectedList>
            <P weight="semibold" marginBottom={0.5}>
              <Trans>
                This location will also be removed from {alsoRemoved.length}{' '}
                linked integration(s):
              </Trans>
            </P>
            <SyncStatusTable rows={alsoRemoved} />
            <P size="small" shade={1} marginTop={0.5} marginBottom={0}>
              <Trans>
                Their configured values are discarded too, and are not restored
                if the location is added back.
              </Trans>
            </P>
          </AffectedList>
        )}
      </Flex>
    </Modal>
  );
}
RemoveLocationModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  sharedSettings: PropTypes.arrayOf(PropTypes.object),
  sharedSettingsLoading: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  integration: PropTypes.objectOf(PropTypes.any).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  location: PropTypes.objectOf(PropTypes.any).isRequired,
};
